// Enhanced Session Manager for Anonymous Karaoke Users
// This allows end users to track their requests without creating accounts
// Now integrates with DJ session ID for better persistence

class SessionManager {
  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.djSessionId = null;
    this.karaokeHostId = null;
  }

  // Generate or retrieve session ID
  getOrCreateSessionId() {
    let sessionId = localStorage.getItem('karaoke_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('karaoke_session_id', sessionId);
    }
    return sessionId;
  }

  // Set DJ session context for better tracking
  setDjSession(djSessionId, karaokeHostId) {
    this.djSessionId = djSessionId;
    this.karaokeHostId = karaokeHostId;
    
    // Store DJ session info for persistence
    localStorage.setItem('karaoke_dj_session_id', djSessionId);
    localStorage.setItem('karaoke_host_id', karaokeHostId);
    
    // Update storage key to include DJ session
    this.storageKey = `karaoke_session_${this.sessionId}_${djSessionId}`;
  }

  // Get DJ session info from storage
  getDjSession() {
    const djSessionId = localStorage.getItem('karaoke_dj_session_id');
    const karaokeHostId = localStorage.getItem('karaoke_host_id');
    return { djSessionId, karaokeHostId };
  }

  // Store a request ID for this session
  addRequest(requestId, requestData) {
    const requests = this.getRequests();
    requests[requestId] = {
      ...requestData,
      sessionId: this.sessionId,
      djSessionId: this.djSessionId,
      karaokeHostId: this.karaokeHostId,
      addedAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString()
    };
    this.saveRequests(requests);
  }

  // Update request status (for real-time updates)
  updateRequest(requestId, updates) {
    const requests = this.getRequests();
    if (requests[requestId]) {
      requests[requestId] = {
        ...requests[requestId],
        ...updates,
        lastUpdated: new Date().toISOString()
      };
      this.saveRequests(requests);
    }
  }

  // Get all requests for this session
  getRequests() {
    const stored = localStorage.getItem(this.storageKey);
    return stored ? JSON.parse(stored) : {};
  }

  // Save requests to storage
  saveRequests(requests) {
    localStorage.setItem(this.storageKey, JSON.stringify(requests));
  }

  // Remove a request from session storage
  removeRequest(requestId) {
    const requests = this.getRequests();
    delete requests[requestId];
    this.saveRequests(requests);
  }

  // Clear all session data for current DJ session
  clearCurrentSession() {
    if (this.storageKey) {
      localStorage.removeItem(this.storageKey);
    }
  }

  // Clear all session data (complete reset)
  clearAllSessions() {
    // Clear all karaoke-related storage
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('karaoke_')) {
        localStorage.removeItem(key);
      }
    });
  }

  // Get session ID
  getSessionId() {
    return this.sessionId;
  }

  // Check if request belongs to this session
  hasRequest(requestId) {
    const requests = this.getRequests();
    return requestId in requests;
  }

  // Get request by ID
  getRequest(requestId) {
    const requests = this.getRequests();
    return requests[requestId] || null;
  }

  // Check if we have any requests for current DJ session
  hasAnyRequests() {
    const requests = this.getRequests();
    return Object.keys(requests).length > 0;
  }

  // Get requests count
  getRequestsCount() {
    const requests = this.getRequests();
    return Object.keys(requests).length;
  }

  // Check if DJ session is still valid (for cleanup)
  isDjSessionValid() {
    return this.djSessionId && this.karaokeHostId;
  }
}

export default SessionManager;
